import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import { Autocomplete, Box, Divider, styled, TextField } from '@mui/material';
import { ExpandMoreRounded as ExpandMoreRoundedIcon } from '@mui/icons-material';

import { usePortalContext } from '@/contexts/PortalProvider';

import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';

import {
  ReservationFormInput,
  ReservationFormValues,
} from '../schema/reservationForm.schema';

import * as providerSlice from '@/data/provider.slice';
import type { SortModel } from '@/data/models/sort-model.model';

import TextInput from '@/components/forms/TextInput';
import Checkbox from '@/components/forms/Checkbox';
import SelectMenu from '@/components/forms/SelectMenu';
import PhoneInput from '@/components/forms/PhoneInput';

const PREFIX = 'RequestedProvider';

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

type RequestedProviderProps = {
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>;
};

const RequestedProvider = ({ form }: RequestedProviderProps) => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');
  const { isPortal } = usePortalContext();

  const [providerSearch, setProviderSearch] = useState<string>('');

  const providers = useAppSelector(providerSlice.selectProviderList);

  const requestedProviderId = form.watch('RequestedTransportationProviderID');

  const getProviderOptionLabel = (option: number) => {
    const provider = providers?.items?.find(
      (p) => p.transportationProviderID === option,
    );
    return provider?.transportationProviderName || '';
  };

  useEffect(() => {
    const filter = 'ctt0&active0'; // only get cleared and active providers
    const sortModel: SortModel = {
      column: 'transportationProviderName',
      direction: 'asc',
    };

    dispatch(
      providerSlice.fetchProviderListAsync({
        pageSize: 100,
        page: 1,
        filter,
        search: providerSearch,
        sortModel,
      }),
    );
  }, [providerSearch, dispatch]);

  return (
    <StyledBox>
      {!isPortal && (
        <>
          <Divider />
          <fieldset>
            <legend>{t('reservation.requestProvider')}</legend>
            <Autocomplete
              inputValue={providerSearch}
              onInputChange={(_evt, val) => setProviderSearch(val)}
              options={
                providers?.items?.map(
                  (item) => item.transportationProviderID,
                ) || []
              }
              getOptionLabel={getProviderOptionLabel}
              getOptionKey={(key) => key}
              onChange={(_, val: any) => {
                form.setValue(`RequestedTransportationProviderID`, val);
              }}
              value={requestedProviderId || null}
              includeInputInList
              filterSelectedOptions
              popupIcon={<ExpandMoreRoundedIcon />}
              noOptionsText={
                providerSearch
                  ? t('common.noProviders')
                  : t('common.typeToSearchProvider')
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('reservation.provider')}
                  variant="outlined"
                />
              )}
            />
          </fieldset>
        </>
      )}
    </StyledBox>
  );
};

export { RequestedProvider };
